import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image, Font } from '@react-pdf/renderer';
import { getBrandingForEmployee } from '../config/brandingStore';
import { formatDate } from '../utils/dateUtils';

Font.register({
  family: 'Amiri',
  src: require('../assets/Amiri.ttf'),
});

const LeaveAcceptedPDF = ({ data }) => {
  const employee = data?.employee;
  const leave = data?.leave;
  const refText = data?.ref || '';
  const location = data?.location || '';
  
  // Obtenir le branding selon le site de l'employé
  const BRAND = getBrandingForEmployee(employee);
  const createdAt = leave?.created_at || data?.createdAt || Date.now();
  const headerCity = location || BRAND?.pdfHeader?.city || '';
  const headerDateLabel = BRAND?.pdfHeader?.dateLabel || 'Le';
  const headerDateStr = formatDate(createdAt);
  const topRightText = headerCity ? `${headerCity} , ${headerDateLabel} ${headerDateStr}` : `${headerDateLabel} ${headerDateStr}`;
  const fullName = `${employee?.last_name || ''} ${employee?.first_name || ''}`.trim();
  
  // Utiliser directement employee.poste pour le poste
  const poste = employee?.poste || data?.poste || '.............';
  
  const exercice = data?.fiscal_year || '';

  // Dates and computed inclusive days
  const startDateObj = leave?.start_date ? new Date(leave.start_date) : null;
  const endDateObj = leave?.end_date ? new Date(leave.end_date) : null;
  const periodeDu = startDateObj ? formatDate(startDateObj) : '';
  const periodeAu = endDateObj ? formatDate(endDateObj) : '';
  
  const computedDays = (startDateObj && endDateObj)
    ? Math.max(1, Math.round((endDateObj - startDateObj) / (1000 * 60 * 60 * 24)) + 1)
    : null;
  
  // Priorité: utiliser computedDays si disponible, sinon leave.days
  const demandeJours = computedDays || leave?.days || '';

  // Entitlement and remaining days logic
  const type = (leave?.type || '').toLowerCase();
  const droitCongesRaw = (type === 'annuel')
    ? (employee?.leave_balance ?? data?.entitlement_days ?? '')
    : (data?.entitlement_days ?? '');
  const parseNum = (v) => {
    if (v == null) return NaN;
    if (typeof v === 'number') return v;
    const m = String(v).match(/[-+]?[0-9]*\.?[0-9]+/);
    return m ? Number(m[0]) : NaN;
  };
  const droitCongesNum = parseNum(droitCongesRaw);
  const demandeJoursNum = parseNum(demandeJours);
  const droitConges = Number.isNaN(droitCongesNum) ? '' : droitCongesNum;
  let resteAPrendre = (data?.remaining_days != null) ? data.remaining_days : (
    (!Number.isNaN(droitCongesNum) && !Number.isNaN(demandeJoursNum))
      ? Math.max(0, droitCongesNum - demandeJoursNum)
      : ''
  );

  // Return-to-work date: next business day (skip Fri/Sat and optional holidays)
  const holidays = new Set((data?.holidays || []).map((d) => new Date(d).toDateString()));
  const isWeekend = (d) => {
    const day = d.getDay(); // 0 Sun ... 6 Sat
    return day === 5 || day === 6; // Fri (5), Sat (6)
  };
  const isHoliday = (d) => holidays.has(d.toDateString());
  const nextBusinessDay = (d) => {
    const n = new Date(d);
    n.setDate(n.getDate() + 1);
    while (isWeekend(n) || isHoliday(n)) {
      n.setDate(n.getDate() + 1);
    }
    return n;
  };
  let repriseTravail = data?.return_to_work_date ? new Date(data.return_to_work_date) : null;
  if (!repriseTravail && endDateObj) repriseTravail = nextBusinessDay(endDateObj);
  const repriseTravailStr = repriseTravail ? formatDate(repriseTravail) : '';

  // Extra mapping to match the photo's wording
  const pvNumber = data?.pv_number || '';
  const pvDate = formatDate(data?.pv_date) || '';
  const pvPerson = data?.pv_person || '';
  const companyName = data?.company_name || '2MP INDUSTRY Spa';
  const exerciseLabel = data?.exercise_label || (exercice ? `de l’exercice ${exercice}` : '');

  // Compute exercice string if not provided
  let exerciceComputed = exercice;
  if (!exerciceComputed && startDateObj) {
    const y = startDateObj.getFullYear();
    exerciceComputed = `${y}/${y + 1}`;
  }

  // Dynamic label for the line "Prendre une congé ... de X jours" based on type
  const typePhr = (() => {
    switch (type) {
      case 'annuel': return 'annuel payé';
      case 'maternite': return 'de maternité';
      case 'recuperation': return 'de récupération';
      case 'sans_solde': return 'sans solde';
      case 'exceptionnel': return 'exceptionnel';
      case 'autre': return `(${leave?.reason || '……'})`;
      default: return '';
    }
  })();

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.container}>
          {/* Header: keep existing */}
          <View style={styles.logo}>
            <Image src={BRAND.pdfLogo} style={styles.logoImage} />
          </View>

          {/* Optional small header row under logo for ref/date if provided */}
          {(refText || topRightText) && (
            <View style={styles.topRow}>
              <Text style={styles.topLeft}>{refText ? `Ref : ${refText}` : ''}</Text>
              <Text style={styles.topRight}>{topRightText}</Text>
            </View>
          )}

          {/* Main title */}
          <Text style={styles.title}>TITRE DE CONGE N° {leave?.reference_conge ? (() => {
            const refNumber = leave.reference_conge;
            // Si la référence contient déjà un slash, l'utiliser telle quelle
            if (refNumber.includes('/')) {
              return refNumber;
            }
            // Sinon, formater comme 00XX/YYYY
            const currentYear = new Date(leave.created_at || Date.now()).getFullYear();
            return `${String(refNumber).padStart(4, '0')}/${currentYear}`;
          })() : '........'}</Text>

          {/* Bulleted context list (customizable via branding templates) */}
          {(() => {
            // Template rendering with placeholders
            const renderTpl = (tpl, ctx) => {
              if (!tpl) return '';
              return tpl.replace(/\{(\w+)\}/g, (_, k) => (ctx[k] ?? ''));
            };
            const defaultLaw = '– Vu la loi n°90-11 du 21/04/1991, modifiée et complétée, relative aux relations de travail.';
            const defaultPV = '– Vu le procès-verbal de nomination{pvNumberOpt}{pvDateOpt}{pvPersonOpt} en qualité du président du Conseil d’Administration de la société {companyName}.';
            const defaultReg = '– Vu les dispositions règlementaires.';
            const defaultLeave = '– Vu la demande d\'absences/congés payés{exerciseLabelOpt}.';

            const pvNumberOpt = pvNumber ? ` N° ${pvNumber}` : '';
            const pvDateOpt = pvDate ? ` du ${pvDate}` : '';
            const pvPersonOpt = pvPerson ? ` de ${pvPerson}` : '';
            const exerciseLabelOpt = exerciseLabel ? ` au titre ${exerciseLabel}` : '';

            const ctx = { pvNumber, pvDate, pvPerson, companyName, exerciseLabel, pvNumberOpt, pvDateOpt, pvPersonOpt, exerciseLabelOpt };
            const brandPV = BRAND?.leaveAccepted?.pvBullet;
            const brandReg = BRAND?.leaveAccepted?.regulatoryBullet;

            const pvLine = brandPV ? '– '+renderTpl(brandPV, ctx) : renderTpl(defaultPV, ctx);
            const regLine = brandReg ? renderTpl(brandReg, ctx) : defaultReg;

            return (
              <View style={styles.bullets}>
                <Text style={styles.bulletItem}>{defaultLaw}</Text>
                <Text style={styles.bulletItem}>{pvLine}</Text>
                <Text style={styles.bulletItem}>{regLine}</Text>
                <Text style={styles.bulletItem}>{renderTpl(defaultLeave, ctx)}</Text>
              </View>
            );
          })()}

          {/* Centered AUTORISE with underline like the photo */}
          <Text style={styles.autorise}>***  <Text style={styles.autoriseUnderline}>AUTORISE</Text>  ***</Text>

          {/* Article Unique block: centered container, left-aligned text */}
          <View style={[styles.articleBlock, styles.articleCentered]}>
            <View style={styles.articleIndented}>
              <Text style={styles.line}><Text style={styles.label}>Nom Complet :</Text> {fullName || '........'}</Text>
              <Text style={styles.line}><Text style={styles.label}>Prendre un congé {typePhr || 'annuel payé'} de</Text> {demandeJours || '........'} jours</Text>
              <Text style={styles.line}><Text style={styles.label}>Poste :</Text> {poste}</Text>
              <Text style={styles.line}><Text style={styles.label}>Droit au congé :</Text> {droitConges || '........'} jour</Text>
              <Text style={styles.line}><Text style={styles.label}>Exercice :</Text> {exerciceComputed || '.../....'}</Text>
              <Text style={styles.line}><Text style={styles.label}>Nombre de jours de congé demandé :</Text> {demandeJours || '........'} jours</Text>
              <Text style={styles.line}><Text style={styles.label}>Pour la période allant du :</Text> {periodeDu || '........'} <Text style={styles.label}>Au</Text> {periodeAu || '..........'}</Text>
              <Text style={styles.line}><Text style={styles.label}>Reste à prendre (congé) :</Text> {resteAPrendre || '0'} jours.</Text>
              <Text style={[styles.line, { marginTop: 8 }]}><Text style={styles.label}>Date de reprise du travail le :</Text> {repriseTravailStr || '........'}</Text>
            </View>
          </View>

          {/* Footer signature */}
          <View style={{ marginTop: 18 }}>
            <Text style={styles.footerRight}>2MP INDUSTRY</Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};

const styles = StyleSheet.create({
  page: {
    padding: 40,
    fontFamily: 'Times-Roman',
    fontSize: 11,
    lineHeight: 1.5,
    display: 'flex',
    flexDirection: 'column',
  },
  container: { flexGrow: 1, display: 'flex', flexDirection: 'column' },
  logo: { width: '100%', marginBottom: 10 },
  logoImage: { width: '100%' },
  topRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 12 },
  topLeft: { fontSize: 10 },
  topRight: { fontSize: 10 },
  title: { marginTop: 10, fontSize: 26, textAlign: 'center', fontWeight: 'bold', marginBottom: 14 },
  bullets: { marginTop: 6, marginBottom: 12, paddingLeft: 16 },
  bulletItem: { fontSize: 11, marginBottom: 4 },
  autorise: { textAlign: 'center', marginVertical: 10, fontSize: 14, fontWeight: 'bold' },
  autoriseUnderline: { textDecoration: 'underline' },
  articleBlock: { marginTop: 8, paddingLeft: 10 },
  articleCentered: { width: '80%', alignSelf: 'center', paddingLeft: 0 },
  articleTitle: { fontSize: 12, textDecoration: 'underline', marginBottom: 6, fontWeight: 'bold' },
  // Approximate left padding to align after the label "Article Unique :"
  articleIndented: { paddingLeft: 95 },
  line: { fontSize: 12, marginBottom: 4 },
  centerLine: { textAlign: 'center' },
  indentLine: { paddingLeft: 24 },
  label: { fontWeight: 'bold' },
  footerRight: { fontSize: 12, textAlign: 'right', marginTop: 8, fontWeight: 'bold' },
});

export default LeaveAcceptedPDF;
